// Per-seller Click & Drop connection + shipment creation.
//
// SECURITY:
//  - The encrypted API key columns are hidden from clients by a column-level
//    REVOKE and are only touched via `supabaseAdmin` inside these server-fn
//    handlers, after the caller has been authenticated by `requireSupabaseAuth`.
//  - The plaintext API key is only ever held in memory inside a single
//    handler invocation and never returned to the browser.
//  - Client-reachable module: DO NOT import shipping-crypto.server or
//    client.server at module scope; load inside handlers.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const SafeSettingsSchema = z.object({
  sender_name: z.string().trim().max(120).nullish(),
  sender_email: z
    .string()
    .trim()
    .max(255)
    .nullish()
    .refine((v) => !v || /.+@.+\..+/.test(v), "Invalid email"),
  sender_phone: z.string().trim().max(30).nullish(),
  sender_line1: z.string().trim().max(200).nullish(),
  sender_line2: z.string().trim().max(200).nullish(),
  sender_city: z.string().trim().max(120).nullish(),
  sender_county: z.string().trim().max(120).nullish(),
  sender_postcode: z.string().trim().max(20).nullish(),
  sender_country: z.string().trim().max(2).nullish(),
  default_weight_grams: z.number().int().min(1).max(30000),
  default_package_format: z.string().max(40),
  default_service: z.string().max(40),
});

export const getSellerShipping = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      // safe view — never exposes ciphertext columns
      .from("seller_shipping_connections_safe" as never)
      .select("*")
      .eq("seller_id", userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  });

export const saveSellerShipping = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => SafeSettingsSchema.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const patch = {
      ...data,
      seller_id: userId,
      sender_country: (data.sender_country || "GB").toUpperCase(),
    };
    // Owner-only RLS lets the authenticated user upsert their own row; the
    // credential columns are UPDATE-revoked so this call cannot touch them
    // even if the payload attempted to include them.
    const { error } = await supabase
      .from("seller_shipping_connections")
      .upsert(patch, { onConflict: "seller_id" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const saveRoyalMailConnection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ apiKey: z.string().trim().min(10).max(500) }).parse(d))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const { encryptSecret } = await import("./shipping-crypto.server");
    const { validateRoyalMailConnection } = await import("./royal-mail.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Validate with Royal Mail first so we never persist a bogus key as connected.
    let validation: Awaited<ReturnType<typeof validateRoyalMailConnection>>;
    try {
      validation = await validateRoyalMailConnection(data.apiKey);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      validation = { ok: false, error: msg, status: 0 };
    }
    const enc = encryptSecret(data.apiKey);
    const { error } = await supabaseAdmin
      .from("seller_shipping_connections")
      .upsert(
        {
          seller_id: userId,
          api_key_ciphertext: enc.ciphertext,
          api_key_iv: enc.iv,
          api_key_tag: enc.tag,
          api_key_last4: enc.last4,
          status: validation.ok ? "connected" : "invalid",
          last_validated_at: new Date().toISOString(),
          last_validation_error: validation.ok ? null : validation.error,
        },
        { onConflict: "seller_id" },
      );
    if (error) throw new Error(error.message);
    return { ok: validation.ok, status: validation.ok ? "connected" : "invalid", error: validation.ok ? null : validation.error };
  });

export const testRoyalMailConnection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const { decryptSecret } = await import("./shipping-crypto.server");
    const { validateRoyalMailConnection } = await import("./royal-mail.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data, error } = await supabaseAdmin
      .from("seller_shipping_connections")
      .select("api_key_ciphertext,api_key_iv,api_key_tag")
      .eq("seller_id", userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data?.api_key_ciphertext || !data.api_key_iv || !data.api_key_tag) {
      return { ok: false, error: "No Royal Mail API key on file" } as const;
    }
    const apiKey = decryptSecret(data.api_key_ciphertext, data.api_key_iv, data.api_key_tag);
    const v = await validateRoyalMailConnection(apiKey);
    await supabaseAdmin
      .from("seller_shipping_connections")
      .update({
        status: v.ok ? "connected" : "invalid",
        last_validated_at: new Date().toISOString(),
        last_validation_error: v.ok ? null : v.error,
      })
      .eq("seller_id", userId);
    return v;
  });

export const disconnectRoyalMail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("seller_shipping_connections")
      .update({
        api_key_ciphertext: null,
        api_key_iv: null,
        api_key_tag: null,
        api_key_last4: null,
        status: "disconnected",
        last_validated_at: null,
        last_validation_error: null,
      })
      .eq("seller_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const OverridesSchema = z
  .object({
    service: z.string().max(40).optional(),
    packageFormat: z.string().max(40).optional(),
    weightGrams: z.number().int().positive().max(30000).optional(),
    lengthMm: z.number().int().nonnegative().max(2000).optional(),
    widthMm: z.number().int().nonnegative().max(2000).optional(),
    depthMm: z.number().int().nonnegative().max(2000).optional(),
  })
  .optional();

export const createShipment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({ orderId: z.string().uuid(), overrides: OverridesSchema })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // 1. Load order (RLS: seller can only read their own; buyer can read theirs).
    const { data: order, error: oe } = await supabase
      .from("orders")
      .select(
        "id,status,seller_id,buyer_id,amount_pence,shipping_method,shipping_address,buyer_name,buyer_email,buyer_phone,auctions(id,title,weight_grams)",
      )
      .eq("id", data.orderId)
      .maybeSingle();
    if (oe) throw new Error(oe.message);
    if (!order) throw new Error("Order not found");
    if (order.seller_id !== userId) throw new Error("Not your order");
    if (!["paid", "ready_to_ship", "exported_to_click_drop"].includes(order.status)) {
      throw new Error(`Order status "${order.status}" cannot be labelled`);
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // 2. Load seller's Click & Drop connection.
    const { data: conn } = await supabaseAdmin
      .from("seller_shipping_connections")
      .select("*")
      .eq("seller_id", userId)
      .maybeSingle();
    if (!conn?.api_key_ciphertext) {
      throw new Error("Connect your Royal Mail account in Shipping settings first");
    }
    if (conn.status !== "connected") {
      throw new Error("Your Royal Mail connection is not valid — reconnect it in Shipping settings");
    }

    // 3. Idempotency — reuse the row for this order if we've already touched it.
    const { data: existing } = await supabaseAdmin
      .from("shipments")
      .select("id,status,tracking_number,label_storage_path,provider_order_identifier")
      .eq("order_id", order.id)
      .neq("status", "cancelled")
      .maybeSingle();
    if (existing && existing.status !== "failed") {
      const { data: signed } = existing.label_storage_path
        ? await supabaseAdmin.storage
            .from("shipping-labels")
            .createSignedUrl(existing.label_storage_path, 60 * 60 * 24 * 7)
        : { data: null as { signedUrl: string } | null };
      return {
        ok: true,
        shipmentId: existing.id,
        trackingNumber: existing.tracking_number,
        labelUrl: signed?.signedUrl ?? null,
        duplicate: true,
      };
    }

    // 4. Assemble Royal Mail payload.
    const { decryptSecret } = await import("./shipping-crypto.server");
    const rm = await import("./royal-mail.server");
    const apiKey = decryptSecret(conn.api_key_ciphertext, conn.api_key_iv!, conn.api_key_tag!);

    const service =
      data.overrides?.service ?? conn.default_service ?? order.shipping_method ?? "royal_mail_tracked_48";
    const packageFormat = data.overrides?.packageFormat ?? conn.default_package_format ?? "smallParcel";
    const auction = (order as unknown as { auctions?: { weight_grams?: number; title?: string } }).auctions;
    const weightGrams =
      data.overrides?.weightGrams ?? conn.default_weight_grams ?? auction?.weight_grams ?? 100;
    const serviceCode = rm.SERVICE_CODE_MAP[service] ?? undefined;
    const ref = order.id.slice(0, 8).toUpperCase();

    const shipTo = order.shipping_address as { name?: string; phone?: string; email?: string; address?: Record<string, string> } | null;
    const addr = shipTo?.address ?? {};
    if (!addr.line1 || !addr.postal_code) throw new Error("Order has no shipping address on file");

    const idempotencyKey = `rm:${order.id}`;
    const shipmentInsert = {
      order_id: order.id,
      seller_id: userId,
      provider: "royal_mail",
      service_code: serviceCode ?? null,
      service_label: service,
      package_format: packageFormat,
      weight_grams: weightGrams,
      length_mm: data.overrides?.lengthMm ?? null,
      width_mm: data.overrides?.widthMm ?? null,
      depth_mm: data.overrides?.depthMm ?? null,
      status: "pending" as const,
      idempotency_key: idempotencyKey,
      last_error: null as string | null,
    };

    let shipmentId: string;
    if (existing?.status === "failed") {
      shipmentId = existing.id;
      const { error: uErr } = await supabaseAdmin
        .from("shipments")
        .update(shipmentInsert)
        .eq("id", existing.id);
      if (uErr) throw new Error(uErr.message);
    } else {
      const { data: sh, error: se } = await supabaseAdmin
        .from("shipments")
        .insert(shipmentInsert)
        .select("id")
        .single();
      if (se) throw new Error(se.message);
      shipmentId = sh.id;
    }

    const payload = {
      items: [
        {
          orderReference: ref,
          recipient: {
            address: {
              fullName: order.buyer_name ?? shipTo?.name ?? "",
              addressLine1: addr.line1,
              addressLine2: addr.line2 ?? "",
              city: addr.city ?? "",
              county: addr.state ?? "",
              postcode: addr.postal_code,
              countryCode: (addr.country ?? "GB").toUpperCase(),
            },
            phoneNumber: order.buyer_phone ?? shipTo?.phone ?? undefined,
            emailAddress: order.buyer_email ?? shipTo?.email ?? undefined,
          },
          sender:
            conn.sender_name || conn.sender_line1
              ? {
                  address: {
                    fullName: conn.sender_name ?? "",
                    addressLine1: conn.sender_line1 ?? "",
                    addressLine2: conn.sender_line2 ?? "",
                    city: conn.sender_city ?? "",
                    county: conn.sender_county ?? "",
                    postcode: conn.sender_postcode ?? "",
                    countryCode: (conn.sender_country ?? "GB").toUpperCase(),
                  },
                  phoneNumber: conn.sender_phone ?? undefined,
                  emailAddress: conn.sender_email ?? undefined,
                }
              : undefined,
          packages: [
            {
              weightInGrams: weightGrams,
              packageFormatIdentifier: packageFormat,
              dimensions: data.overrides?.lengthMm
                ? {
                    heightInMms: data.overrides.depthMm ?? 0,
                    widthInMms: data.overrides.widthMm ?? 0,
                    lengthInMms: data.overrides.lengthMm ?? 0,
                  }
                : undefined,
            },
          ],
          orderDate: new Date().toISOString(),
          subtotal: (order.amount_pence / 100).toFixed(2),
          shippingCostCharged: "0.00",
          total: (order.amount_pence / 100).toFixed(2),
          currencyCode: "GBP",
          shippingServiceCode: serviceCode,
          label: { includeLabelInResponse: true, includeCN: false, includeReturnsLabel: false },
        },
      ],
    };

    try {
      const rmRes = await rm.createRoyalMailOrder(apiKey, payload);
      if (!rmRes.labelBase64) {
        throw new rm.RoyalMailError(502, "no_label", "Royal Mail did not return a label");
      }
      const bytes = Uint8Array.from(atob(rmRes.labelBase64), (c) => c.charCodeAt(0));
      const objectPath = `${userId}/${order.id}.pdf`;
      const { error: upErr } = await supabaseAdmin.storage
        .from("shipping-labels")
        .upload(objectPath, bytes, { contentType: "application/pdf", upsert: true });
      if (upErr) throw new Error(`Label upload failed: ${upErr.message}`);

      await supabaseAdmin
        .from("shipments")
        .update({
          status: "label_created",
          tracking_number: rmRes.trackingNumber,
          provider_order_identifier: rmRes.orderIdentifier ?? ref,
          label_storage_path: objectPath,
          last_error: null,
        })
        .eq("id", shipmentId);

      // Preserve the legacy order status flip so existing UI stays intact.
      await supabaseAdmin
        .from("orders")
        .update({
          status: "ready_to_ship",
          royal_mail_last_error: null,
          royal_mail_last_attempt_at: new Date().toISOString(),
        })
        .eq("id", order.id);

      await supabaseAdmin.from("shipment_events").insert({
        shipment_id: shipmentId,
        event_type: "label_created",
        http_status: 200,
        metadata: {
          service_code: serviceCode ?? null,
          service: service,
          weight_grams: weightGrams,
          package_format: packageFormat,
        },
      });

      const { data: signed } = await supabaseAdmin.storage
        .from("shipping-labels")
        .createSignedUrl(objectPath, 60 * 60 * 24 * 7);

      return {
        ok: true,
        shipmentId,
        trackingNumber: rmRes.trackingNumber,
        labelUrl: signed?.signedUrl ?? null,
        duplicate: false,
      };
    } catch (e) {
      const raw = e instanceof Error ? e.message : String(e);
      const msg = rm.redact(raw);
      const httpStatus = e instanceof rm.RoyalMailError ? e.status : null;
      const code = e instanceof rm.RoyalMailError ? e.code : "unknown";
      await supabaseAdmin
        .from("shipments")
        .update({ status: "failed", last_error: msg.slice(0, 2000) })
        .eq("id", shipmentId);
      await supabaseAdmin.from("shipment_events").insert({
        shipment_id: shipmentId,
        event_type: "api_error",
        http_status: httpStatus,
        message: msg.slice(0, 2000),
        metadata: { code },
      });
      await supabaseAdmin
        .from("orders")
        .update({
          royal_mail_last_error: msg.slice(0, 2000),
          royal_mail_last_attempt_at: new Date().toISOString(),
        })
        .eq("id", order.id);
      throw new Error(msg);
    }
  });

export const getOrderShipment = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ orderId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    // RLS allows the buyer, seller, and admins to read the shipment. Only
    // non-credential fields exist on the shipments table, so this is safe
    // to return directly.
    const { data: sh, error } = await context.supabase
      .from("shipments")
      .select(
        "id,order_id,provider,tracking_number,service_code,service_label,package_format,weight_grams,status,dispatched_at,last_tracking_update_at,created_at,updated_at",
      )
      .eq("order_id", data.orderId)
      .neq("status", "cancelled")
      .maybeSingle();
    if (error) throw new Error(error.message);
    return sh ?? null;
  });
